// ============================================================================
// Supply Management — Approval Queue
// AMs + execs + superusers approve or reject Submitted supply requests for
// their assigned facilities. Sorted by urgency (Emergency first, red strip),
// then oldest first. RLS limits the underlying SELECT to facilities the AM
// owns; the approve/reject buttons call SECURITY DEFINER RPCs that also
// enforce is_my_facility() server-side.
//
// Flat-fetch + client-join pattern matches supply-requests.tsx. See
// memory/tcs_flat_fetch_pattern_2026_05_16.md.
// ============================================================================

import { canApproveSupplyRequest } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../shared/auth';
import { supabase } from '../../shared/supabase';
import { ErrorState, PageHeader, UrgencyBadge } from '../../shared/components';
import type { SupplyRequest, SupplyUrgency } from '../../shared/types';
import { SupplySubNav } from './supply-sub-nav';
import { errorMessage } from '../../shared/errors';
import { approveSupplyRequest, rejectSupplyRequest } from './approvals';

interface QueueRow extends SupplyRequest {
  facility_name: string | null;
  supply_name: string | null;
  requester_name: string | null;
}

const URGENCY_ORDER: Record<SupplyUrgency, number> = {
  Emergency: 0,
  Soon: 1,
  Planned: 2,
};


export default function ApprovalQueue() {
  const { profile } = useAuth();
  const role = profile?.role;
  const canApprove = canApproveSupplyRequest(role);

  const [rows, setRows] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: flat, error: e } = await supabase
        .from('supply_requests')
        .select(
          `request_id, request_batch_id, facility_id, account_id, supply_id,
           item_description, quantity, unit_of_measure, urgency, date_needed,
           requested_by, approved_by, approved_at, ship_to_address, reimbursable,
           notes, status, estimated_cost, vendor_selected, created_at, updated_at`,
        )
        .eq('status', 'Submitted')
        .order('created_at', { ascending: true });

      if (e) throw e;
      const flatRows = (flat ?? []) as unknown as SupplyRequest[];

      const facIds = Array.from(
        new Set(flatRows.map((r) => r.facility_id).filter(Boolean)),
      ) as string[];
      const supIds = Array.from(
        new Set(flatRows.map((r) => r.supply_id).filter(Boolean)),
      ) as string[];
      const empIds = Array.from(
        new Set(flatRows.map((r) => r.requested_by).filter(Boolean)),
      ) as string[];

      const [facRes, supRes, empRes] = await Promise.all([
        facIds.length === 0
          ? Promise.resolve({ data: [] as unknown[], error: null })
          : supabase
              .from('facilities')
              .select('facility_id, facility_name')
              .in('facility_id', facIds),
        supIds.length === 0
          ? Promise.resolve({ data: [] as unknown[], error: null })
          : supabase
              .from('supplies')
              .select('supply_id, item_name')
              .in('supply_id', supIds),
        empIds.length === 0
          ? Promise.resolve({ data: [] as unknown[], error: null })
          : supabase
              .from('v_employees_am')
              .select('employee_id, employee_name_first, employee_name_last')
              .in('employee_id', empIds),
      ]);

      const facMap: Record<string, string | null> = {};
      for (const f of (facRes.data ?? []) as Array<{
        facility_id: string;
        facility_name: string | null;
      }>) {
        facMap[f.facility_id] = f.facility_name;
      }
      const supMap: Record<string, string | null> = {};
      for (const s of (supRes.data ?? []) as Array<{
        supply_id: string;
        item_name: string | null;
      }>) {
        supMap[s.supply_id] = s.item_name;
      }
      const empMap: Record<string, string> = {};
      for (const e of (empRes.data ?? []) as Array<{
        employee_id: string;
        employee_name_first: string | null;
        employee_name_last: string | null;
      }>) {
        empMap[e.employee_id] = [e.employee_name_first, e.employee_name_last]
          .filter(Boolean)
          .join(' ')
          .trim();
      }

      const enriched: QueueRow[] = flatRows.map((r) => ({
        ...r,
        facility_name: r.facility_id ? facMap[r.facility_id] ?? null : null,
        supply_name: r.supply_id ? supMap[r.supply_id] ?? null : null,
        requester_name: r.requested_by ? empMap[r.requested_by] ?? null : null,
      }));

      setRows(enriched);
    } catch (e) {
      console.error('[approval-queue] load failed', e);
      setError(errorMessage(e, 'Failed to load queue.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Group by request_batch_id for display only. NULL batch_id => each row is
  // its own group (historical, pre-multi-item rows). Non-NULL batch_id =>
  // all sibling rows collapse into one expandable card. Per-row approval is
  // preserved either way.
  interface QueueGroup {
    key: string;
    batchId: string | null;
    rows: QueueRow[];
    urgencyRank: number; // worst (lowest) tier of any row, drives ordering + badge
    oldestCreated: string;
    totalEstimatedCost: number | null;
    facilityName: string | null;
    facilityId: string | null;
    requesterName: string | null;
    requestedBy: string | null;
  }

  const groupedRows = useMemo<QueueGroup[]>(() => {
    const buckets = new Map<string, QueueRow[]>();
    for (const r of rows) {
      // NULL batch_id rows each get a unique synthetic key so they render
      // standalone — same behavior as the old per-row queue.
      const key = r.request_batch_id ?? `single:${r.request_id}`;
      const arr = buckets.get(key);
      if (arr) arr.push(r);
      else buckets.set(key, [r]);
    }

    const groups: QueueGroup[] = [];
    for (const [key, rs] of buckets.entries()) {
      let worstRank = 3;
      let oldest = rs[0].created_at ?? '';
      let totalCost: number | null = null;
      for (const r of rs) {
        const rank = URGENCY_ORDER[r.urgency as SupplyUrgency] ?? 3;
        if (rank < worstRank) worstRank = rank;
        if ((r.created_at ?? '') < oldest) oldest = r.created_at ?? '';
        if (r.estimated_cost != null) {
          totalCost = (totalCost ?? 0) + Number(r.estimated_cost);
        }
      }
      groups.push({
        key,
        batchId: rs[0].request_batch_id ?? null,
        rows: rs,
        urgencyRank: worstRank,
        oldestCreated: oldest,
        totalEstimatedCost: totalCost,
        facilityName: rs[0].facility_name,
        facilityId: rs[0].facility_id,
        requesterName: rs[0].requester_name,
        requestedBy: rs[0].requested_by,
      });
    }

    groups.sort((a, b) => {
      if (a.urgencyRank !== b.urgencyRank) return a.urgencyRank - b.urgencyRank;
      return (a.oldestCreated ?? '').localeCompare(b.oldestCreated ?? '');
    });
    return groups;
  }, [rows]);

  const pendingCount = rows.length;

  const onApprove = async (r: QueueRow) => {
    setBusyId(r.request_id);
    try {
      await approveSupplyRequest(r.request_id);
      await load();
    } catch (e) {
      alert(`Approve failed: ${errorMessage(e, String(e))}`);
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (r: QueueRow) => {
    const reason = window.prompt('Rejection reason (required):', '');
    if (reason == null || reason.trim().length === 0) return;
    setBusyId(r.request_id);
    try {
      await rejectSupplyRequest(r.request_id, reason);
      await load();
    } catch (e) {
      alert(`Reject failed: ${errorMessage(e, String(e))}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <SupplySubNav />
      <PageHeader
        title="Supply approvals"
        meta={
          loading
            ? 'Loading…'
            : `${pendingCount} pending · ${groupedRows.length} request${groupedRows.length === 1 ? '' : 's'}`
        }
      />

      {!canApprove && (
        <div className="mt-4 rounded-md border border-st-warn-border bg-st-warn-bg p-4 text-sm text-st-warn-fg-strong">
          Your role can't approve supply requests. Talk to an account manager.
        </div>
      )}

      {error && (
        <div className="mt-4">
          <ErrorState body={error} onRetry={load} />
        </div>
      )}

      {!loading && groupedRows.length === 0 && (
        <div className="mt-4 rounded-md border border-tcs-border bg-tcs-surface p-6 text-center text-sm text-tcs-text-secondary">
          No pending requests. All caught up.
        </div>
      )}

      <ul className="mt-4 space-y-3">
        {groupedRows.map((g) => {
          // Find the worst-tier row's urgency for the badge.
          const worstUrgency =
            g.rows.find(
              (r) => (URGENCY_ORDER[r.urgency as SupplyUrgency] ?? 3) === g.urgencyRank,
            )?.urgency ?? g.rows[0].urgency;
          const isEmergency = worstUrgency === 'Emergency';
          return (
            <BatchCard
              key={g.key}
              group={g}
              worstUrgency={worstUrgency as SupplyUrgency}
              isEmergency={isEmergency}
              canApprove={canApprove}
              busyId={busyId}
              onApprove={onApprove}
              onReject={onReject}
            />
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BatchCard — collapsed view shows facility + line count + total cost +
// worst-tier urgency badge; expanded view shows each line as a sub-row with
// its own Approve/Reject buttons.
// ---------------------------------------------------------------------------

function BatchCard({
  group,
  worstUrgency,
  isEmergency,
  canApprove,
  busyId,
  onApprove,
  onReject,
}: {
  group: {
    key: string;
    batchId: string | null;
    rows: QueueRow[];
    urgencyRank: number;
    oldestCreated: string;
    totalEstimatedCost: number | null;
    facilityName: string | null;
    facilityId: string | null;
    requesterName: string | null;
    requestedBy: string | null;
  };
  worstUrgency: SupplyUrgency;
  isEmergency: boolean;
  canApprove: boolean;
  busyId: string | null;
  onApprove: (r: QueueRow) => Promise<void>;
  onReject: (r: QueueRow) => Promise<void>;
}) {
  // Batch cards default to expanded; single-line groups have nothing to
  // collapse, so we open them implicitly. The toggle is still available for
  // ≥2-row batches.
  const isMulti = group.rows.length > 1;
  const [expanded, setExpanded] = useState(true);
  const showLines = !isMulti || expanded;
  const r0 = group.rows[0];

  return (
    <li
      className={[
        'rounded-md border bg-tcs-surface p-4',
        isEmergency
          ? 'border-st-danger-border-strong ring-1 ring-st-danger-border-strong'
          : 'border-tcs-border',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <UrgencyBadge tier={worstUrgency} />
            {isMulti ? (
              <span className="rounded bg-tcs-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
                Batch · {group.rows.length} items
              </span>
            ) : (
              <span className="font-mono text-xs text-tcs-text-secondary">
                {r0.request_id}
              </span>
            )}
            {isMulti && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-xs text-tcs-accent-ink underline hover:no-underline"
              >
                {expanded ? 'Collapse' : 'Expand'}
              </button>
            )}
          </div>
          <div className="mt-2 text-sm font-medium text-tcs-text">
            {group.facilityName ?? group.facilityId ?? 'Unknown facility'}
          </div>
          <div className="mt-1 text-xs text-tcs-text-secondary">
            {group.requesterName ?? group.requestedBy ?? 'Unknown requester'}
            {r0.date_needed ? ` · needed by ${r0.date_needed}` : ''}
            {group.totalEstimatedCost != null
              ? ` · est $${group.totalEstimatedCost.toFixed(2)}`
              : ''}
          </div>
        </div>
      </div>

      {showLines && (
        <ul className="mt-3 space-y-2">
          {group.rows.map((r) => (
            <li
              key={r.request_id}
              className="rounded-md border border-tcs-border bg-tcs-raised p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <UrgencyBadge tier={r.urgency as SupplyUrgency} />
                    <span className="font-mono text-[11px] text-tcs-text-secondary">
                      {r.request_id}
                    </span>
                  </div>
                  <div className="mt-2 text-sm font-medium text-tcs-text">
                    {r.quantity} × {r.unit_of_measure ?? ''}{' '}
                    {r.supply_name ?? r.item_description}
                  </div>
                  <div className="mt-1 text-xs text-tcs-text-secondary">
                    {r.estimated_cost != null
                      ? `est $${Number(r.estimated_cost).toFixed(2)}`
                      : 'est —'}
                    {r.reimbursable ? ' · reimbursable' : ''}
                  </div>
                  {r.notes && (
                    <div className="mt-2 text-xs text-tcs-text-secondary line-clamp-2">
                      {r.notes}
                    </div>
                  )}
                </div>
                {canApprove && (
                  <div className="flex flex-shrink-0 gap-2">
                    <button
                      disabled={busyId === r.request_id}
                      onClick={() => onReject(r)}
                      className="rounded-md border border-st-danger-border-strong bg-tcs-raised px-3 py-1.5 text-sm font-medium text-st-danger-fg hover:bg-st-danger-bg disabled:opacity-50"
                    >
                      Reject
                    </button>
                    <button
                      disabled={busyId === r.request_id}
                      onClick={() => onApprove(r)}
                      className="rounded-md bg-st-ok-solid px-3 py-1.5 text-sm font-medium text-st-solid-ink hover:bg-st-ok-solid disabled:opacity-50"
                    >
                      Approve
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
